import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import DashboardPage from "@/pages/admin/DashboardPage";
import ServicesPage from "@/pages/admin/ServicesPage";
import EmployeesPage from "@/pages/admin/EmployeesPage";
import ProductsPage from "@/pages/admin/ProductsPage";
import GalleryManagePage from "@/pages/admin/GalleryManagePage";
import WhatsAppPage from "@/pages/admin/WhatsAppPage";
import OrdersPage from "@/pages/admin/OrdersPage";
import RecruitmentPage from "@/pages/admin/RecruitmentPage";
import CustomersPage from "@/pages/admin/CustomersPage";
import RefundPage from "@/pages/admin/RefundPage";
import AdminTestimonialPage from "@/pages/admin/AdminTestimonialPage";
import DeveloperPage from "@/pages/admin/DeveloperPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import FinancePage from "@/pages/admin/FinancePage";
import DailyReportPage from "@/pages/admin/DailyReportPage";
import StatisticsPage from "@/pages/admin/StatisticsPage";
import POSPage from "@/pages/pos/POSPage";
import StorePage from "@/pages/StorePage";
import CartPage from "@/pages/CartPage";
import GalleryPage from "@/pages/GalleryPage";
import CustomerPortal from "@/pages/CustomerPortal";
import RecruitmentPublicPage from "@/pages/RecruitmentPublicPage";
import TestimonialPage from "@/pages/TestimonialPage";
import PaymentStatusPage from "@/pages/PaymentStatusPage";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  useEffect(() => {
    const removeBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
      document.querySelectorAll('a[href*="emergent.sh"]').forEach(e => e.remove());
    };
    removeBadge();
    const interval = setInterval(removeBadge, 300);
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(interval); observer.disconnect(); };
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/toko" element={<StorePage />} />
          <Route path="/galeri" element={<GalleryPage />} />
          <Route path="/karir" element={<RecruitmentPublicPage />} />
          <Route path="/testimonial" element={<TestimonialPage />} />
          <Route path="/payment/success" element={<PaymentStatusPage status="success" />} />
          <Route path="/payment/pending" element={<PaymentStatusPage status="pending" />} />
          <Route path="/payment/failed" element={<PaymentStatusPage status="failed" />} />
          <Route path="/keranjang" element={<ProtectedRoute allowedRoles={["customer"]}><CartPage /></ProtectedRoute>} />
          <Route path="/portal" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerPortal /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute allowedRoles={["administrator", "finance", "store_manager", "kepala_cabang"]}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="daily-report" element={<DailyReportPage />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="gallery" element={<GalleryManagePage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="whatsapp" element={<WhatsAppPage />} />
            <Route path="recruitment" element={<RecruitmentPage />} />
            <Route path="refund" element={<RefundPage />} />
            <Route path="testimonial" element={<AdminTestimonialPage />} />
            <Route path="developer" element={<DeveloperPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="/pos" element={<ProtectedRoute allowedRoles={["administrator", "kasir", "kepala_cabang"]}><POSPage /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
